// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@ces/App/Store/Reducers";
import LazyPicture from "@ces/MobiusOverrides/LazyImage/LazyPicture";
import { getSystemColor, mergeWithCss } from "@ces/XDL/Utilities";
import { getFocalPointStyles, parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { responsiveStyleRules } from "@insite/client-framework/Common/Utilities/responsive";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const getObjectPosition = (focalPoint?: string) => {
    switch (focalPoint) {
        case "topLeft":
            return "left top";
        case "topCenter":
            return "center top";
        case "topRight":
            return "right top";
        case "centerLeft":
            return "left center";
        case "centerRight":
            return "right center";
        case "bottomLeft":
            return "left bottom";
        case "bottomCenter":
            return "center bottom";
        case "bottomRight":
            return "right bottom";
        default:
            return "center center";
    }
};

const enum fields {
    background = "background",
    focalPoint = "focalPoint",
    backgroundColor = "backgroundColor",
    heading = "heading",
    subheading = "subheading",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonVariant = "variant",
    bannerWidth = "bannerWidth",
    imageOverlay = "imageOverlay",
    partialOverlay = "partialOverlay",
    partialOverlayPositioning = "partialOverlayPositioning",
    disableButton = "disableButton",
    centerTextVertically = "centerTextVertically",
    responsiveFontSizes = "responsiveFontSizes",
    customFontSizes = "customFontSizes",
    h1FontSize = "h1FontSize",
    h2FontSize = "h2FontSize",
    h3FontSize = "h3FontSize",
    h4FontSize = "h4FontSize",
    h5FontSize = "h5FontSize",
    h6FontSize = "h6FontSize",
    normalFontSize = "normalFontSize",
    customCSS = "customCSS",
    // XNG-Change-CES-263
    mainLargeImage = "mainLargeImage",
    mainMediumImage = "mainMediumImage",
    mainSmallImage = "mainSmallImage",
    mainImageAltText = "mainImageAltText",
    mainImageWidth = "mainImageWidth",
    isReversedImage = "isReversedImage",
    gapBetweenContents = "gapBetweenContents",
    isBGSolidColor = "isBGSolidColor",
    horizontalAlignment = "horizontalAlignment",
    linkColor = "linkColor",
    maxContentWidth = "maxContentWidth",
    contentPaddingTopBottom = "contentPaddingTopBottom",
    contentPaddingLeftRight = "contentPaddingLeftRight",
    isContentStretched = "isContentStretched",
    showMainImageOnSmallScreenOnly = "showMainImageOnSmallScreenOnly",
    showImageBGOnLargeScreen = "showImageBGOnLargeScreen",
    openInNewTab = "openInNewTab",
    buttonSize = "buttonSize",
    // XNG-change-267
    largeBackgroundImage = "largeBackgroundImage",
    mediumBackgroundImage = "mediumBackgroundImage",
    smallBackgroundImage = "smallBackgroundImage",
    isResponsiveImage = "isResponsiveImage",
    mediumImageHeight = "mediumImageHeight",
    smallImageHeight = "smallImageHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.background]: "image" | "color";
        [fields.focalPoint]:
            | "topLeft"
            | "topCenter"
            | "topRight"
            | "centerLeft"
            | "center"
            | "centerRight"
            | "bottomLeft"
            | "bottomCenter"
            | "bottomRight";
        [fields.backgroundColor]: string;
        [fields.heading]: string;
        [fields.subheading]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonVariant]: "primary" | "secondary" | "tertiary" | "link";
        [fields.imageOverlay]: string;
        [fields.partialOverlay]: boolean;
        [fields.partialOverlayPositioning]: "top" | "middle" | "bottom";
        [fields.disableButton]: boolean;
        [fields.centerTextVertically]: boolean;
        [fields.responsiveFontSizes]: boolean;
        [fields.customFontSizes]: boolean;
        [fields.h1FontSize]: number;
        [fields.h2FontSize]: number;
        [fields.h3FontSize]: number;
        [fields.h4FontSize]: number;
        [fields.h5FontSize]: number;
        [fields.normalFontSize]: number;
        [fields.customCSS]: string;
        // XNG-Change-CES-263
        [fields.mainLargeImage]: string;
        [fields.mainMediumImage]: string;
        [fields.mainSmallImage]: string;
        [fields.mainImageAltText]: string;
        [fields.mainImageWidth]: number;
        [fields.isReversedImage]: boolean;
        [fields.gapBetweenContents]: number;
        [fields.isBGSolidColor]: boolean;
        [fields.horizontalAlignment]: "left" | "center" | "right";
        [fields.linkColor]: string;
        [fields.maxContentWidth]: number;
        [fields.contentPaddingTopBottom]: number;
        [fields.contentPaddingLeftRight]: number;
        [fields.isContentStretched]: boolean;
        [fields.showMainImageOnSmallScreenOnly]: boolean;
        [fields.showImageBGOnLargeScreen]: boolean;
        [fields.openInNewTab]: boolean;
        [fields.buttonSize]: "small" | "medium" | "large";
        // XNG-change-267
        [fields.largeBackgroundImage]: string;
        [fields.mediumBackgroundImage]: string;
        [fields.smallBackgroundImage]: string;
        [fields.isResponsiveImage]: boolean;
        [fields.mediumImageHeight]: string;
        [fields.smallImageHeight]: string;
    };
    extendedStyles?: BannerStyles;
}

export interface BannerStyles {
    wrapper?: InjectableCss;
    overlayWrapper?: InjectableCss;
    /**
     * @deprecated Use the `bannerButton` property instead.
     */
    slideCenteringWrapperStyles?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerButton?: ButtonPresentationProps;
    // XNG-Change-CES-263
    mainContainer?: InjectableCss;
    mainImageWrapper?: InjectableCss;
    mainContentWrapper?: InjectableCss;
    contentWrapper?: InjectableCss;
    mainImage?: LazyImageProps;
    bannerTitle?: TypographyPresentationProps;
    bannerDescription?: TypographyPresentationProps;
}

export const bannerStyles: BannerStyles = {
    wrapper: {
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    overlayWrapper: {
        css: css`
            width: 100%;
            color: white;
            text-align: center;
        `,
    },
};

export const Banner: FC<OwnProps> = ({ fields, extendedStyles }) => {
    const { url, title } = useGetLink(fields.buttonLink);
    const history = useHistory();
    const { isInShell } = useShellContext();
    // XNG-Change-CES-263
    const isTabletScreen = useAppSelector(state => (state as CustomAppState)?.xCommon?.isTabletScreen);
    const isMobileScreen = useAppSelector(state => (state as CustomAppState)?.xCommon?.isMobileScreen);

    const onClick = () => {
        if (!url) {
            return;
        }

        // XNG-Change-CES-263
        if (fields.openInNewTab) {
            window.open(url, "_blank", "noreferrer");
        } else if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }

            window.location.href = url;
        } else {
            history.push(url);
        }
    };

    // XNG-Change-CES-263
    const hasBackgroundImage = fields.background === "image";
    const hasAnyBackgroundImage =
        hasBackgroundImage &&
        !!(fields.largeBackgroundImage || fields.mediumBackgroundImage || fields.smallBackgroundImage);
    const focalPointObjectPosition = getObjectPosition(fields.focalPoint);
    const backgroundStyles =
        hasBackgroundImage && (!isTabletScreen || fields.showImageBGOnLargeScreen)
            ? `height: ${
                  fields.isResponsiveImage && isMobileScreen
                      ? `${fields.smallImageHeight}`
                      : fields.isResponsiveImage && isTabletScreen
                      ? `${fields.mediumImageHeight}`
                      : "100%"
              };`
            : fields.isBGSolidColor || isTabletScreen
            ? `background: ${fields.backgroundColor};`
            : `background: linear-gradient(270deg, ${getSystemColor("common.ui11")} 0%, ${getSystemColor(
                  "common.white",
              )} 100%);`;

    const focalPointStyles = getFocalPointStyles(fields.focalPoint);

    let overlayPositioningStyles;
    if (!fields.partialOverlay) {
        overlayPositioningStyles = "align-items: stretch;";
    } else if (fields.partialOverlayPositioning === "top") {
        overlayPositioningStyles = "align-items: flex-start;";
    } else if (fields.partialOverlayPositioning === "middle") {
        overlayPositioningStyles = "align-items: center;";
    } else if (fields.partialOverlayPositioning === "bottom") {
        overlayPositioningStyles = isTabletScreen && !isMobileScreen ? "align-items: top;" : "align-items: flex-end;";
    }
    // XNG-Change: use merge
    const styles = React.useMemo(() => mergeWithCss(bannerStyles, extendedStyles), []);

    let fontSizeStyles;
    if (fields.responsiveFontSizes || fields.customFontSizes) {
        fontSizeStyles = responsiveStyleRules(fields.responsiveFontSizes, fields.customFontSizes ? fields : undefined);
    }

    // XNG-Change-CES-263
    const maxImageContainerWidth = `${fields.mainImageWidth}%`;
    const maxBannerContainerWidth = `${100 - Number(fields.mainImageWidth)}%`;

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    const wrapperStyles = {
        css: css`
            ${styles.wrapper?.css || ""}
            ${backgroundStyles}
            ${focalPointStyles}
            ${overlayPositioningStyles}
            ${fontSizeStyles}
            position: relative;
            .banner-background-picture {
                position: absolute;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 0;
                ${!fields.showImageBGOnLargeScreen ? "@media (max-width: 1024px) { display: none; }" : ""}
            }
            .banner-background-img {
                height: 100%;
                object-position: ${focalPointObjectPosition};
                display: block;
            }
            .banner-overlay-wrapper {
                position: relative;
                z-index: 1;
            }
        `,
    };

    // XNG-Change-CES-263
    const overlayWrapperStyles = {
        css: css`
            ${styles.overlayWrapper?.css || ""}
            background-color: ${fields.background === "image" ? fields.imageOverlay : ""};
            padding: ${fields.contentPaddingTopBottom}px ${fields.contentPaddingLeftRight}px;
            ${fields.centerTextVertically &&
            `
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;`}
        `,
    };

    return (
        <StyledWrapper
            {...styles.mainContainer}
            {...{
                gapBetweenContents: fields.gapBetweenContents && fields.mainLargeImage ? fields.gapBetweenContents : 0,
                isReversedImage: fields.isReversedImage,
            }}
        >
            {/* XNG-Change-CES-263 */}
            {fields.mainLargeImage && (isTabletScreen || !fields.showMainImageOnSmallScreenOnly) && (
                <StyledWrapper
                    {...styles.mainImageWrapper}
                    {...{ maxImageContainerWidth: fields.mainLargeImage ? maxImageContainerWidth : 0 }}
                    className="banner-main-image-wrapper"
                >
                    <LazyPicture
                        disableLazyLoad
                        smallImageSrc={fields.mainSmallImage || fields.mainLargeImage}
                        mediumImageSrc={fields.mainMediumImage || fields.mainLargeImage}
                        largeImageSrc={fields.mainLargeImage}
                        altText={fields.mainImageAltText || "Main Image"}
                        {...styles.mainImage}
                    />
                </StyledWrapper>
            )}
            {/* XNG-Change-CES-263 */}
            <StyledWrapper
                {...customCssWrapper}
                {...styles.mainContentWrapper}
                {...{ maxBannerContainerWidth: fields.mainLargeImage ? maxBannerContainerWidth : "100%" }}
            >
                <StyledWrapper className="banner-wrapper" {...wrapperStyles}>
                    {hasAnyBackgroundImage && (
                        <LazyPicture
                            disableLazyLoad
                            largeImageSrc={fields.largeBackgroundImage}
                            mediumImageSrc={fields.mediumBackgroundImage}
                            smallImageSrc={fields.smallBackgroundImage}
                            altText={fields.mainImageAltText || "Background Image"}
                            pictureProps={{ className: "banner-background-picture" }}
                            imgProps={{
                                className: "banner-background-img",
                                decoding: "async",
                                ...{ loading: "eager" },
                            }}
                        />
                    )}
                    <StyledWrapper
                        className="banner-overlay-wrapper"
                        {...overlayWrapperStyles}
                        {...{
                            horizontalAlignment: fields.horizontalAlignment,
                            maxContentWidth: fields.maxContentWidth,
                            isResponsiveImage: fields.isResponsiveImage,
                            isContentStretched: fields.isContentStretched,
                        }}
                    >
                        <StyledWrapper
                            className="banner-center-wrapper"
                            {...styles.contentWrapper}
                            {...{ isContentStretched: fields.isContentStretched }}
                        >
                            <Typography
                                className="banner-heading"
                                {...styles.bannerTitle}
                                {...{ disableButton: fields.disableButton }}
                            >
                                {parse(fields.heading, parserOptions)}
                            </Typography>
                            <Typography
                                className="banner-subheading"
                                {...styles.bannerDescription}
                                {...{ disableButton: fields.disableButton }}
                            >
                                {parse(fields.subheading, parserOptions)}
                            </Typography>
                            {!fields.disableButton &&
                                (fields.buttonLabel || title || url) &&
                                (fields.variant === "link" ? (
                                    <Link
                                        {...styles.bannerLink}
                                        onClick={onClick}
                                        target={fields.openInNewTab ? "_blank" : ""}
                                        {...{ linkColor: fields.linkColor }}
                                    >
                                        {fields.buttonLabel || title || url}
                                    </Link>
                                ) : (
                                    <Button
                                        {...styles.bannerButton}
                                        variant={fields.variant}
                                        sizeVariant={fields.buttonSize}
                                        onClick={onClick}
                                    >
                                        {fields.buttonLabel || title || url}
                                    </Button>
                                ))}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.banner-wrapper {
}

.banner-overlay-wrapper {
}

.banner-center-wrapper {
}

.banner-heading {
}

.banner-subheading {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const settingsTab = {
    displayName: "Settings",
    sortOrder: 1,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const banner: WidgetModule = {
    component: Banner,
    definition: {
        group: "Basic",
        icon: "image-landscape",
        fieldDefinitions: [
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.mainLargeImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
                isRequired: false,
            },
            {
                fieldType: "Contextual",
                name: fields.mainImageAltText,
                editorTemplate: "TextField",
                defaultValue: "",
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.mainMediumImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
                isRequired: false,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.mainSmallImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                tab: basicTab,
                isRequired: false,
            },
            // XNG-Change-CES-263
            {
                name: fields.mainImageWidth,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 0,
                max: 100,
                defaultValue: 50,
                isVisible: widget => widget.fields.mainLargeImage,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.showMainImageOnSmallScreenOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.showImageBGOnLargeScreen,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.background,
                displayName: "Background",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Image", value: "image" },
                    { displayName: "Color", value: "color" },
                ],
                defaultValue: "image",
                hideEmptyOption: true,
                tab: basicTab,
            },
            // XNG-change-267
            {
                fieldType: "Contextual",
                name: fields.largeBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // XNG-change-267
            {
                fieldType: "Contextual",
                name: fields.mediumBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // XNG-change-267
            {
                fieldType: "Contextual",
                name: fields.smallBackgroundImage,
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // xng-change-CES-267
            {
                fieldType: "Contextual",
                name: fields.isResponsiveImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // xng-change-CES-267
            {
                fieldType: "Contextual",
                name: fields.mediumImageHeight,
                editorTemplate: "TextField",
                displayName: "Medium Image Height",
                defaultValue: "219px",
                placeholder: "10 px for example",
                tab: basicTab,
                isVisible: widget => widget.fields.isResponsiveImage,
            },
            // xng-change-CES-267
            {
                fieldType: "Contextual",
                name: fields.smallImageHeight,
                editorTemplate: "TextField",
                displayName: "small Image Height",
                defaultValue: "393px",
                placeholder: "10 px for example",
                tab: basicTab,
                isVisible: widget => widget.fields.isResponsiveImage,
            },
            {
                fieldType: "Contextual",
                name: fields.imageOverlay,
                displayName: "Image Color Overlay",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlay,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.partialOverlayPositioning,
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top", value: "top" },
                    { displayName: "Middle", value: "middle" },
                    { displayName: "Bottom", value: "bottom" },
                ],
                hideEmptyOption: true,
                defaultValue: "bottom",
                isVisible: widget => widget.fields.partialOverlay,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.focalPoint,
                displayName: "Focal Point",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Top Left", value: "topLeft" },
                    { displayName: "Top Center", value: "topCenter" },
                    { displayName: "Top Right", value: "topRight" },
                    { displayName: "Center Left", value: "centerLeft" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Center Right", value: "centerRight" },
                    { displayName: "Bottom Left", value: "bottomLeft" },
                    { displayName: "Bottom Center", value: "bottomCenter" },
                    { displayName: "Bottom Right", value: "bottomRight" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                isVisible: widget => widget.fields.background === "image",
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Color",
                defaultValue: "#E7E7E8",
                isVisible: widget => widget.fields.background === "color",
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.isBGSolidColor,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
                isVisible: widget => widget.fields.background === "color",
            },
            {
                fieldType: "Contextual",
                name: fields.heading,
                editorTemplate: "RichTextField",
                displayName: "Heading",
                defaultValue: "",
                extendedConfig: { height: 100 },
                expandedToolbarButtons: {
                    moreMisc: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.subheading,
                editorTemplate: "RichTextField",
                displayName: "Subheading",
                defaultValue: "",
                extendedConfig: { height: 170 },
                expandedToolbarButtons: {
                    moreMisc: {},
                },
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                name: fields.buttonVariant,
                displayName: "Button Variant",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Primary", value: "primary" },
                    { displayName: "Secondary", value: "secondary" },
                    { displayName: "Tertiary", value: "tertiary" },
                    { displayName: "Link", value: "link" },
                ],
                hideEmptyOption: true,
                defaultValue: "primary",
                fieldType: "Contextual",
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                name: fields.buttonSize,
                displayName: "Button Size",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Small", value: "small" },
                    { displayName: "Medium", value: "medium" },
                    { displayName: "Large", value: "large" },
                ],
                hideEmptyOption: true,
                defaultValue: "medium",
                fieldType: "Contextual",
                isVisible: widget => widget.fields.variant !== "link",
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
                isVisible: widget => widget.fields.variant === "link",
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.linkColor,
                editorTemplate: "ColorPickerField",
                displayName: "Link Color",
                defaultValue: "white",
                isVisible: widget => widget.fields.variant === "link",
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.disableButton,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.isReversedImage,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
                isVisible: widget => widget.fields.mainLargeImage,
            },
            // XNG-Change-CES-263
            {
                name: fields.gapBetweenContents,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 0,
                max: 100,
                defaultValue: 0,
                isVisible: widget => widget.fields.mainLargeImage,
            },
            {
                name: fields.responsiveFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: true,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPaddingTopBottom,
                displayName: "Content Padding Top Bottom",
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.contentPaddingLeftRight,
                displayName: "Content Padding Left Right",
                editorTemplate: "IntegerField",
                defaultValue: 50,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.maxContentWidth,
                editorTemplate: "IntegerField",
                defaultValue: 100,
                min: 0,
                max: 100,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: fields.horizontalAlignment,
                displayName: "Horizontal Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "left", value: "left" },
                    { displayName: "center", value: "center" },
                    { displayName: "right", value: "right" },
                ],
                defaultValue: "center",
                hideEmptyOption: true,
                tab: basicTab,
            },
            {
                fieldType: "Contextual",
                name: "centerTextVertically",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            // XNG-Change-CES-263
            {
                fieldType: "Contextual",
                name: fields.isContentStretched,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                tab: basicTab,
            },
            {
                name: fields.customFontSizes,
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                tab: settingsTab,
                defaultValue: false,
            },
            {
                name: fields.normalFontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h1FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 40,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h2FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: 32,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h3FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h4FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h5FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.h6FontSize,
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                tab: settingsTab,
                min: 1,
                defaultValue: null,
                isVisible: item => item?.fields[fields.customFontSizes],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default banner;
